import { useQuery } from "@apollo/client";
import { Loader } from "@components/loader";
import { TopNavigation } from "@components/top-navigation";
import { GET_APP_CONFIG_FOR_FORM, GET_FORM_META_DATA } from "@queries/index";
import { FC, useEffect, useMemo } from "react";
import { Outlet, useParams } from "react-router";
import { PermissionTypes } from "@archetypes/gql";
import "./studio-layout.scss";
import { useOutletContext } from "react-router-dom";
import { Permission } from "@contexts/permissions.context";
import { useNavigate } from "react-router-dom";
import { useToastContext } from "@contexts/toast";
import { AppData, AppFormConfig } from "@archetypes/app";
import { UFormData } from "@archetypes/form";
import { useTitle } from "@contexts/title.context";
import { AWSUser } from "@archetypes/auth";

const StudioLayoutContainer: FC = () => {
  const { formId, appId } = useParams();

  const { setTitle } = useTitle();
  const user = useOutletContext<AWSUser>();

  const { data: appData, loading: isAppLoading } = useQuery<{
    getApp: AppData;
  }>(GET_APP_CONFIG_FOR_FORM, {
    variables: { input: { appId } },
    fetchPolicy: "network-only",
    onError: () => {
      showToast({
        title: "Error",
        content: `Error fetching app details`,
        variant: "danger",
      });
      navigate("/builder/apps");
    },
  });

  const { data: formData, loading: isFormLoading } = useQuery<{ getForm: UFormData }>(
    GET_FORM_META_DATA,
    {
      variables: {
        input: {
          formId,
          appId,
        },
      },
      fetchPolicy: "network-only",
      onError: () => {
        showToast({
          title: "Error",
          content: `Error fetching form details`,
          variant: "danger",
        });
        navigate("/builder/apps");
      },
    },
  );

  const navigate = useNavigate();
  const { showToast } = useToastContext();

  useEffect(() => {
    if (formData?.getForm) {
      setTitle(formData?.getForm.formName);
    }
  }, [formData, setTitle]);

  const context: AppFormConfig | undefined = useMemo(() => {
    if (!appData?.getApp || !formData?.getForm) {
      return;
    }
    const { _id, appName, appLogo, fontColor, primaryColor, favicon, secondaryColor } =
      appData.getApp;
    const { currentStatus, type } = formData.getForm;
    return {
      _id,
      appName,
      appLogo,
      fontColor,
      primaryColor,
      favicon,
      secondaryColor,
      currentStatus,
      type,
      user,
    };
  }, [appData, formData, user]);

  if (isAppLoading || isFormLoading) {
    return <Loader />;
  }

  return (
    <>
      <div className="playground-header">
        <TopNavigation
          formName={formData?.getForm?.formName}
          formDescription={formData?.getForm?.formDescription}
          actions={formData?.getForm?.currentAction?.action}
        />
      </div>
      <div className="playground-body">
        <Outlet context={context} />
      </div>
    </>
  );
};

const StudioLayout: FC = () => {
  return (
    <Permission access={[PermissionTypes.FORM_READ]} redirectUrl="/builder/settings/profile">
      <StudioLayoutContainer />
    </Permission>
  );
};

export default StudioLayout;
